import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SpendingCalendar = ({ transactions }) => {
  const [viewMode, setViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState('2026-01-17');

  const getDailyTotal = (date) => {
    const dayTransactions = transactions?.filter(tx => tx?.date === date);
    return dayTransactions?.reduce((sum, tx) => sum + Math.abs(tx?.amount), 0);
  };

  const getSpendingColor = (amount) => {
    if (amount === 0) return 'bg-muted/30';
    if (amount < 50) return 'bg-success/20';
    if (amount < 100) return 'bg-warning/20';
    return 'bg-error/20';
  };

  const generateCalendarDays = () => {
    const days = [];
    for (let i = 14; i >= 0; i--) {
      const date = new Date('2026-01-18');
      date?.setDate(date?.getDate() - i);
      const dateStr = date?.toISOString()?.split('T')?.[0];
      const total = getDailyTotal(dateStr);
      days?.push({
        date: dateStr,
        day: date?.getDate(),
        dayName: date?.toLocaleDateString('en-US', { weekday: 'short' }),
        total,
        transactions: transactions?.filter(tx => tx?.date === dateStr)
      });
    }
    return days;
  };

  const calendarDays = generateCalendarDays();
  const selectedDayData = calendarDays?.find(day => day?.date === selectedDate);

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <h2 className="card-title">Spending Calendar</h2>
          <p className="card-description">Daily transaction patterns</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('week')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth ${
              viewMode === 'week' ?'bg-primary text-primary-foreground' :'bg-muted/50 text-muted-foreground hover:bg-muted'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setViewMode('month')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth ${
              viewMode === 'month' ?'bg-primary text-primary-foreground' :'bg-muted/50 text-muted-foreground hover:bg-muted'
            }`}
          >
            Month
          </button>
        </div>
      </div>
      <div className="card-content">
        <div className="grid grid-cols-7 gap-2 mb-6">
          {calendarDays?.slice(-7)?.map((day) => (
            <button
              key={day?.date}
              onClick={() => setSelectedDate(day?.date)}
              className={`p-3 rounded-lg transition-smooth ${
                selectedDate === day?.date
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted/30 hover:bg-muted/50'
              }`}
            >
              <div className="text-center">
                <p className="text-xs font-medium mb-1 opacity-70">{day?.dayName}</p>
                <p className="text-lg font-bold mb-2">{day?.day}</p>
                <div className={`h-1.5 rounded-full mx-auto ${
                  selectedDate === day?.date ? 'bg-primary-foreground/30' : getSpendingColor(day?.total)
                }`} style={{ width: '80%' }} />
                <p className="text-xs font-semibold mt-2">
                  {day?.total > 0 ? `$${day?.total?.toFixed(0)}` : '-'}
                </p>
              </div>
            </button>
          ))}
        </div>

        {selectedDayData && (
          <div className="p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">
                  {new Date(selectedDate)?.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="text-2xl font-bold text-foreground">
                  ${selectedDayData?.total?.toFixed(2)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground mb-1">Transactions</p>
                <p className="text-2xl font-bold text-foreground">{selectedDayData?.transactions?.length}</p>
              </div>
            </div>

            {selectedDayData?.transactions?.length > 0 ? (
              <div className="space-y-2">
                {selectedDayData?.transactions?.map((tx) => (
                  <div key={tx?.id} className="flex items-center gap-3 p-3 bg-card rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name={tx?.icon} size={16} className="text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-foreground">{tx?.merchant}</p>
                      <p className="text-xs text-muted-foreground">{tx?.category} • {tx?.time}</p>
                    </div>
                    <p className="text-sm font-bold text-error">-${Math.abs(tx?.amount)?.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No transactions on this day</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SpendingCalendar;